import { useEffect, useRef, useState } from 'react';
import { api, ApiError, type LabelMatch } from '../api';
import { Modal } from './ui';

/** The current camera picture as a JPEG, at most 1600 px across. */
function grabFrame(v: HTMLVideoElement): Promise<Blob | null> {
  if (!v.videoWidth) return Promise.resolve(null);
  const scale = Math.min(1, 1600 / Math.max(v.videoWidth, v.videoHeight));
  const c = document.createElement('canvas');
  c.width = Math.round(v.videoWidth * scale);
  c.height = Math.round(v.videoHeight * scale);
  c.getContext('2d')!.drawImage(v, 0, 0, c.width, c.height);
  return new Promise((resolve) => c.toBlob(resolve, 'image/jpeg', 0.85));
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Product scans: the printed item number ("ITM. / ART.") is read from the camera too. */
export interface ItemNumberReading {
  /** A barcode was scanned. True when that settled it (the camera closes); false to keep reading the item number. */
  barcode: (code: string) => Promise<boolean>;
  /** Products whose item number is printed on the label, with the unlinked barcode if one was scanned; [] = searching by hand. */
  found: (code: string | null, matches: LabelMatch[]) => void;
}

/** A barcode alone is usually quicker; the label is read too once this long has passed without one. */
const READ_LABEL_AFTER_MS = 1500;

/**
 * Camera barcode scanning. Uses the browser's BarcodeDetector where available
 * (Chrome/Android), otherwise ZXing (works on iPhone Safari). Manual entry and
 * USB/Bluetooth scanners always remain available, so this is optional.
 *
 * With `reading` (product barcodes), the camera also sends pictures to the server while it is
 * open, which reads the item number printed on the Costco label. Whichever is found first wins:
 * a linked barcode, or the item number (when the barcode isn't linked or won't scan).
 */
export function CameraScanner({
  title,
  onResult,
  onClose,
  reading,
}: {
  title: string;
  onResult: (code: string) => void;
  onClose: () => void;
  reading?: ItemNumberReading;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [unlinked, setUnlinked] = useState<string | null>(null);
  const [busyReading, setBusyReading] = useState(false);
  const props = useRef({ onResult, onClose, reading });
  props.current = { onResult, onClose, reading };
  const giveUp = useRef<() => void>(() => {});

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let zxingControls: { stop: () => void } | null = null;
    let timer: number | undefined;
    const aborter = new AbortController();
    const withLabel = !!props.current.reading;
    let pending: string | null = null; // a scanned barcode that isn't linked to a product
    let checking = false;
    const buzz = () => 'vibrate' in navigator && navigator.vibrate?.(80);
    const stop = () => {
      stopped = true;
      aborter.abort();
    };

    const onCode = async (text: string) => {
      if (stopped) return;
      const r = props.current.reading;
      if (!r) {
        stop();
        buzz();
        return props.current.onResult(text);
      }
      if (checking || text === pending) return;
      checking = true;
      const settled = await r.barcode(text).catch(() => true);
      checking = false;
      if (stopped) return;
      buzz();
      if (settled) {
        stop();
        return props.current.onClose();
      }
      pending = text;
      setUnlinked(text);
    };
    giveUp.current = () => {
      if (stopped) return;
      stop();
      props.current.reading?.found(pending, []);
      props.current.onClose();
    };

    // Pictures go to the server one at a time, while the camera is open.
    const readLabels = async () => {
      const start = Date.now();
      while (!stopped) {
        const v = videoRef.current;
        if (checking || !v || (!pending && Date.now() - start < READ_LABEL_AFTER_MS)) {
          await sleep(250);
          continue;
        }
        const frame = await grabFrame(v);
        if (!frame) {
          await sleep(250);
          continue;
        }
        const form = new FormData();
        form.append('photo', frame, 'label.jpg');
        setBusyReading(true);
        try {
          const res = await api<{ numbers: string[]; matches: LabelMatch[] }>('/scan/product-label', { form, signal: aborter.signal });
          if (stopped) return;
          if (res.matches.length && !checking) {
            stop();
            buzz();
            props.current.reading?.found(pending, res.matches);
            return props.current.onClose();
          }
        } catch (e) {
          if (stopped) return;
          if (e instanceof ApiError) return setError(`Could not read the item number: ${e.message}. The barcode scan still works.`);
          await sleep(1500); // connection trouble: try again shortly
        } finally {
          if (!stopped) setBusyReading(false);
        }
        await sleep(200);
      }
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser cannot use the camera here. Type the number or use a scanner.');
        return;
      }
      // A sharper picture when the printed number is to be read from it.
      const video: MediaTrackConstraints = withLabel ? { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } : { facingMode: 'environment' };
      const BD = (window as any).BarcodeDetector;
      try {
        if (BD) {
          stream = await navigator.mediaDevices.getUserMedia({ video });
          const v = videoRef.current!;
          v.srcObject = stream;
          await v.play();
          const detector = new BD();
          const tick = async () => {
            if (stopped) return;
            try {
              const codes = await detector.detect(v);
              if (codes[0]?.rawValue) await onCode(codes[0].rawValue);
            } catch {
              /* frame not ready */
            }
            timer = window.setTimeout(tick, 150);
          };
          tick();
        } else {
          const { BrowserMultiFormatReader } = await import('@zxing/browser');
          const reader = new BrowserMultiFormatReader();
          zxingControls = await reader.decodeFromConstraints({ video }, videoRef.current!, (result) => {
            if (result) onCode(result.getText());
          });
        }
        if (withLabel) readLabels();
      } catch (e) {
        setError(`Camera unavailable: ${(e as Error).message}. Type the number or use a scanner instead.`);
      }
    })();

    return () => {
      stop();
      clearTimeout(timer);
      zxingControls?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <Modal title={title} onClose={onClose}>
      {error && <div className="alert alert-warn">{error}</div>}
      {!error && !unlinked && (
        <p className="muted">{reading ? 'Point the camera at the barcode and the ITM. / ART. number.' : 'Point the camera at the barcode.'}</p>
      )}
      {unlinked && (
        <div className="alert alert-info">
          Barcode <span className="mono">{unlinked}</span> isn't linked to a product yet. Keep the <strong>ITM. / ART.</strong> number in view to find it by item number.{' '}
          <button type="button" className="btn btn-small btn-ghost" onClick={() => giveUp.current()}>
            Search by hand instead
          </button>
        </div>
      )}
      <video ref={videoRef} className="camera-video" playsInline muted />
      {reading && busyReading && <div className="muted small">Reading the item number…</div>}
    </Modal>
  );
}
